/**
 * میزِ اتاق: یکی از «میزهای من» — کاربر، ۲۱ اکتبر ۲۰۲۶: «مشتری میزهایی را که در فروشگاه ساخته سرِ
 * ساختنِ اتاق به کار ببرد؛ همه‌ی ساختن در فروشگاه است»، و ۲۲ اکتبر: «هرچه مالِ این امکان است به
 * فروشگاه برود؛ در /play فقط میزهایش را ببیند و یکی را انتخاب کند».
 *
 * پس این‌جا فقط فهرستِ میزهای ساخته‌شده است: نه کاشیِ «قرعه»، نه «میزِ تازه»، نه راهنما. لمسِ میزِ
 * انتخاب‌شده دوباره برش می‌دارد ("" = قرعه سرِ شروع، مثلِ وقتی هیچ میزی نیست). بی میز هیچ چیز
 * کشیده نمی‌شود. مقدار "table:<شناسه>" است و سرور آن را از فهرستِ **خودِ میزبان** به شهر و فرش و
 * پشتِ کارت برمی‌گرداند (ws/hub.go، roomTableOf) — کلیدِ دلخواهِ کلاینت پذیرفته نیست. همه‌ی سرِ میز
 * همان را می‌بینند.
 */
import { useEffect } from "react";
import { client, type MyTableRow } from "../net/client";
import { useSignal } from "../net/signal";
import { tableTitle, viewOf } from "../game/carpet";
import { myTableName, TablePreview } from "./MyTable";

const KEY = "bazivan.table";
/** آینه‌ی match.RoomTablePrefix. */
export const ROOM_TABLE = "table:";

/** انتخابِ دفعه‌ی پیش: "table:<شناسه>" یا "" (قرعه). */
export function loadTableChoice(): string {
  let v = "";
  try { v = localStorage.getItem(KEY) ?? ""; } catch { /* حالتِ ناشناس */ }
  return v.startsWith(ROOM_TABLE) ? v : "";
}

export function saveTableChoice(k: string): void {
  try { localStorage.setItem(KEY, k); } catch { /* حالتِ ناشناس */ }
}

/** میزی که هنوز در «میزهای من» هست، یا "" (قرعه) — شاید از دفعه‌ی پیش پاک شده باشد. */
export function validChoice(k: string): string {
  return k.startsWith(ROOM_TABLE) && (client.myTables.value ?? []).some((t) => ROOM_TABLE + t.id === k) ? k : "";
}

/** میزِ انتخابی از فهرست، اگر هست. */
export function chosenTable(k: string): MyTableRow | undefined {
  return (client.myTables.value ?? []).find((t) => ROOM_TABLE + t.id === k);
}

/** نامِ میزِ اتاق برای خلاصه‌ها و لابی (کلیدِ میز، نه شناسه): «اصفهان با فرشِ لاجورد». */
export function tableName(key: string): string {
  return tableTitle(key.split("@")[0]!);
}

/** نامِ انتخاب برای خلاصه‌ی «بیشتر»: «قرعه» یا نامِ میز. */
export function tableChoiceName(k: string): string {
  const t = chosenTable(k);
  return t ? `میز ${myTableName(t)}` : "قرعه";
}

/** آیا میزی برای انتخاب هست؟ (فهرست هنوز نرسیده = نه.) */
export function hasTables(): boolean {
  return (client.myTables.value ?? []).length > 0;
}

export function TablePicker({ value, onPick }: { value: string; onPick: (k: string) => void }) {
  const tables = useSignal(client.myTables);
  useEffect(() => { if (client.myTables.value === null) void client.loadMyTables(); }, []);
  const list = tables ?? [];
  if (list.length === 0) return null;
  return (
    <div className="tablepick" role="group" aria-label="میزهای من">
      {list.map((t) => {
        const v = viewOf(t.table);
        const on = value === ROOM_TABLE + t.id;
        return (
          <button key={t.id} type="button" aria-pressed={on} className={"tp mine" + (on ? " on" : "")}
                  onClick={() => onPick(on ? "" : ROOM_TABLE + t.id)} aria-label={myTableName(t)}>
            <span className="pic"><TablePreview city={v.city?.key ?? ""} rug={v.rug.key} back={t.back} w={72} /></span>
            <span className="name">{myTableName(t)}</span>
          </button>
        );
      })}
    </div>
  );
}
